// Cookie Management
const cookieUtils = {
    set: (name, value) => {
        if (typeof window === "undefined") return;
        document.cookie = `${name}=${value}; path=/`;
    },
    get: (name) => {
        if (typeof window === "undefined") return "";
        const cookieName = `${name}=`;
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookies = decodedCookie.split(';');
        
        for (const cookie of cookies) {
            const trimmedCookie = cookie.trim();
            if (trimmedCookie.indexOf(cookieName) === 0) {
                return trimmedCookie.substring(cookieName.length);
            }
        }
        return "";
    }
};

export const setSessionId = () => {
    console.log('setSessionId');
    const sessionId = crypto.randomUUID();
    cookieUtils.set("session_id", sessionId);
    return sessionId;
};

export const setDeviceId = () => {
    const deviceId = crypto.randomUUID();
    cookieUtils.set("device_id", deviceId);
    return deviceId;
};

const isStaging = () => {
    return typeof window !== "undefined" && window.location.hostname.includes("staging");
}


// Analytics Configuration
export const ANALYTICS_CONFIG = {
    DB_NAME: "ENDO_ADM_ANALYTICS",
    DB_STORE: "offline_analytics",
    DB_VERSION: 1,
    TIMEZONE: "America/New_York",
    GA_TRACKING_ENABLED: false,
    DYNAMODB_SYNC_ENABLED: true,
    API_URL: isStaging() ? "https://xiaflexadm-fa-staging.azurewebsites.net/api/analytics/collect" : "https://xiaflexadm-fa.azurewebsites.net/api/analytics/collect",
    trackingColumns: [
        "eventId", // eventId MUST be first column
        "sessionId",
        "deviceId",
        // "gaId",
        "timestamp",
        "timestampLocal",
        "event_name",
        "event_action",
        "event_label",
        "source",
        "sentTo" // sentTo should be last column
    ],
    scrollerSelector: ".custom-scrollbars__content",
    scrollTrackingIncrement: 25,
    videoTrackingSecondsIncrement: 10,
    videoTrackingPercentageIncrement: 25
};

export const DEFAULT_VALUES = {
    sessionId: cookieUtils.get("session_id") || setSessionId(),
    gaId: cookieUtils.get("_ga") || "GA_not_implemented",
    deviceId: cookieUtils.get("device_id") || setDeviceId(),
    sendTo: "null",
    formCode: "endo_adm_offline_analytics_sync"
}

const ARD_CONSTANTS = {
    page_view: {
        event_name: "virtual_pageview",
        event_action: "$page_name",
        event_label: "$screen_name",
    },
    cta_clicks: {
        event_name: "cta_clicks",
        event_action: "$button_text",
        event_label: "$screen_name",
    },
    navigation: {
        event_name: "navigation",
        event_action: "$button_text",
        event_label: "$screen_name",
    },
    input_update: {
        event_name: "input_update",
        event_action: "$form_field",
        event_label: "$screen_name",
    },
}

export const api_url_is_valid = () => {
    return ANALYTICS_CONFIG.API_URL && ANALYTICS_CONFIG.API_URL.trim() !== "";
};

// Debug Utilities
export const isDebugMode = () => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("debug") === "true";
};

export const consoleDebug = (...args) => {
    if (isDebugMode()) console.log(...args);
};

// Database Operations
const dbUtils = {
    open: () => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(ANALYTICS_CONFIG.DB_NAME, ANALYTICS_CONFIG.DB_VERSION);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(ANALYTICS_CONFIG.DB_STORE)) {
                    db.createObjectStore(ANALYTICS_CONFIG.DB_STORE, { autoIncrement: true });
                }
            };
            
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.target.error);
        });
    },

    async getAll() {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(ANALYTICS_CONFIG.DB_STORE, 'readonly');
            const store = transaction.objectStore(ANALYTICS_CONFIG.DB_STORE);
            const request = store.getAll();
            
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.target.error);
        });
    },

    async add(entry) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(ANALYTICS_CONFIG.DB_STORE, 'readwrite');
            const store = transaction.objectStore(ANALYTICS_CONFIG.DB_STORE);
            const request = store.add({ entry });
            
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.target.error);
        });
    },

    async update(eventId, updatedData) {
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([ANALYTICS_CONFIG.DB_STORE], 'readwrite');
            const store = transaction.objectStore(ANALYTICS_CONFIG.DB_STORE);

            // Get all entries and their keys
            const getAllRequest = store.getAll();
            const getAllKeys = store.getAllKeys();

            Promise.all([
                new Promise(resolve => { getAllRequest.onsuccess = () => resolve(getAllRequest.result); }),
                new Promise(resolve => { getAllKeys.onsuccess = () => resolve(getAllKeys.result); })
            ]).then(([entries, keys]) => {
                const index = entries.findIndex(entry => {
                    const parts = entry.entry.split('|');
                    return parts[0] === eventId;
                });

                if (index === -1) {
                    reject(`Entry with event_id ${eventId} not found`);
                    return;
                }

                // Update the existing entry using the original key
                const updateRequest = store.put({ 
                    entry: updatedData 
                }, keys[index]); // Specify the key to update the existing entry

                updateRequest.onsuccess = () => {
                    resolve({ success: true });
                };

                updateRequest.onerror = (event) => {
                    reject('Error updating entry: ' + event.target.error);
                };
            });

            transaction.oncomplete = () => {
                db.close();
            };
        });
    }
};

// Tracking Functions
const createTrackingEntry = (name, action, label, timestamp, timestampLocal, source) => {
    const valueMap = {
        eventId: "evt-" + crypto.randomUUID(),
        sessionId: DEFAULT_VALUES.sessionId,
        deviceId: DEFAULT_VALUES.deviceId,
        gaId: DEFAULT_VALUES.gaId,
        timestamp,
        timestampLocal,
        event_name: name,
        event_action: action,
        event_label: label,
        source,
        sentTo: DEFAULT_VALUES.sendTo
    };

    return ANALYTICS_CONFIG.trackingColumns
        .map(column => {
            if (!valueMap[column]) {
                consoleDebug("WARNING: valueMap[column] is undefined for column", column);
            }
            return valueMap[column] || 'n/a'
        })
        .join('|');
};

const getTimestampInTimezone = (timestamp, timezone) => {
    return new Date(timestamp).toLocaleString('en-US', { timeZone: timezone, hour12: false });
};

const trackEvent = (name="", action="", label="") => {
    consoleDebug('%c -----------: ', 'color: blue');
    consoleDebug('%c name: ', 'color: red', name);
    consoleDebug('%c action: ', 'color: red', action);
    consoleDebug('%c label: ', 'color: red', label);
    consoleDebug('%c -----------: ', 'color: blue');

    trackToOfflineDb(name, action, label);

    if (ANALYTICS_CONFIG.GA_TRACKING_ENABLED) {
        trackToGA(name, action, label);
    }
};

export const trackFormInput = (inputLabel, page) => {
    trackEvent(
        ARD_CONSTANTS.input_update.event_name,
        inputLabel,
        page,
    );
};

export const trackCtaClick = (page, buttonText) => {
    trackEvent(
        ARD_CONSTANTS.cta_clicks.event_name,
        buttonText, 
        page, 
    );
};

export const trackNavigation = (page, buttonText) => {
    console.log("trackNavigation", ARD_CONSTANTS.navigation.event_name);
    trackEvent(
        ARD_CONSTANTS.navigation.event_name,
        buttonText, 
        page, 
    );
};

export const trackVirtualPageview = (page) => {
    trackEvent(
        ARD_CONSTANTS.page_view.event_name,
        "/", 
        page, 
    );
};

// Analytics Storage
export const trackToOfflineDb = async (name, action, label) => {
    const source = typeof window !== "undefined" ? window.location.href : "unknown";
    const timestamp = new Date().toISOString();
    const timestampLocal = getTimestampInTimezone(timestamp, ANALYTICS_CONFIG.TIMEZONE);
    try {
        const entry = createTrackingEntry(name, action, label, timestamp, timestampLocal, source);
        await dbUtils.add(entry);
        
        if (isDebugMode()) {
            consoleDebug("tracked to offline db", ANALYTICS_CONFIG.DB_STORE, "|", entry);
        }
    } catch (error) {
        consoleDebug('Failed to open IndexedDB:', error);
    }
};

export const trackToGA = (name, action, label) => {
    if ('dataLayer' in window) {
        window.dataLayer.push({
            "event": `${name} - custom event - sampleclient brand conference`,
            "event_action": action,
            "event_label": label,
            "event_name": name,
        });
    }
};

// Data Export
export const generateAndDownloadCsv = async () => {
    try {
        const data = await dbUtils.getAll();
        const csvData = data.map(item => item.entry.split('|'));
        
        const csvContent = [
            ANALYTICS_CONFIG.trackingColumns.join(","),
            ...csvData.map(row => row.join(','))
        ].join("\r\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        
        link.setAttribute("href", url);
        link.setAttribute("download", `${ANALYTICS_CONFIG.DB_NAME}-_report-${new Date().toISOString()}.csv`);
        link.style.visibility = "hidden";
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        consoleDebug("Error generating CSV:", error);
    }
};

// Data Retrieval and Update
export const getAnalyticsData = async () => {
    try {
        const result = await dbUtils.getAll();
        return { result };
    } catch (error) {
        consoleDebug("Error fetching analytics data:", error);
        throw error;
    }
};

export const updateAnalyticsEntry = async (eventId, updatedData) => {
    try {
        return await dbUtils.update(eventId, updatedData);
    } catch (error) {
        consoleDebug("Error updating analytics entry:", error);
        throw error;
    }
};